import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Safe localStorage that won't throw in private browsing / locked-down mobile browsers.
// Token storage is belt-and-suspenders here: the primary auth is the HttpOnly cookie
// set by /api/auth/login. localStorage is a fallback for environments where third-party
// cookies are blocked.
export const safeStorage = {
  get(key) {
    try {
      return typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem(key)
        : null;
    } catch (err) {
      console.warn("safeStorage.get failed:", err);
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(key, value);
      }
    } catch (err) {
      // Cookie-based auth still works, so this is non-fatal — just log it.
      console.warn("safeStorage.set failed:", err);
    }
  },
  remove(key) {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem(key);
      }
    } catch (err) {
      console.warn("safeStorage.remove failed:", err);
    }
  },
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 20000,
});

// Attach Bearer token if present (belt + suspenders alongside cookies)
api.interceptors.request.use((config) => {
  const token = safeStorage.get("access_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function formatApiError(errOrDetail) {
  // If we got an axios error object, dig into it
  if (errOrDetail && typeof errOrDetail === "object" && (errOrDetail.isAxiosError || errOrDetail.response || errOrDetail.message)) {
    const err = errOrDetail;
    const status = err.response?.status;
    const detail = err.response?.data?.detail;

    if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      return "Can't reach the server. Check your internet connection.";
    }
    if (err.code === "ECONNABORTED") return "Request timed out. Please try again.";

    if (detail) return formatApiError(detail);
    if (status === 401) return "Invalid email or password.";
    if (status === 403) return "You don't have permission to do that.";
    if (status === 404) return "Not found.";
    if (status >= 500) return `Server error (${status}). Please try again.`;
    if (status) return `Request failed (${status}).`;
    return err.message || "Something went wrong. Please try again.";
  }

  const detail = errOrDetail;
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
